import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import zoneService from '../services/zoneService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface ZoneFormData {
  name: string;
  centerLatitude: string;
  centerLongitude: string;
  radiusKm: string; // Use kilometers
  description: string;
  color: string;
  isVisible: boolean;
}

const ZoneFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<ZoneFormData>({
    name: '',
    centerLatitude: '',
    centerLongitude: '',
    radiusKm: '5', // Use kilometers
    description: '',
    color: '#1d388d',
    isVisible: true,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadZone = useCallback(async (zoneId: string) => {
    try {
      const zone = await zoneService.getZoneById(zoneId);
      setFormData({
        name: zone.name,
        centerLatitude: Number(zone.center_latitude).toString(),
        centerLongitude: Number(zone.center_longitude).toString(),
        radiusKm: (zone.radius_meters / 1000).toString(), // Convert meters to km
        description: zone.description || '',
        color: zone.color,
        isVisible: zone.is_visible,
      });
    } catch (error) {
      console.error('Error loading zone:', error);
      toast.error(t('errors.generic'));
      navigate('/zones');
    }
  }, [t, navigate]);

  useEffect(() => {
    if (isEditMode && id) {
      loadZone(id);
    }
  }, [id, isEditMode, loadZone]);

  const handleChange = (field: keyof ZoneFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      isVisible: event.target.checked,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('zone.validation.nameRequired');
    }

    const lat = parseFloat(formData.centerLatitude);
    const lng = parseFloat(formData.centerLongitude);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      newErrors.centerLatitude = t('validation.invalidCoordinates');
    }

    if (isNaN(lng) || lng < -180 || lng > 180) {
      newErrors.centerLongitude = t('validation.invalidCoordinates');
    }

    const radius = parseInt(formData.radiusKm);
    if (isNaN(radius) || radius < 1) {
      newErrors.radiusKm = t('zone.validation.radiusMin');
    }
    if (radius > 1000) {
      newErrors.radiusKm = t('zone.validation.radiusMax');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const zoneData = {
        name: formData.name,
        center_latitude: parseFloat(formData.centerLatitude),
        center_longitude: parseFloat(formData.centerLongitude),
        radius_meters: parseInt(formData.radiusKm) * 1000, // Convert km to meters
        description: formData.description || undefined,
        color: formData.color,
        is_visible: formData.isVisible,
      };

      if (isEditMode && id) {
        await zoneService.updateZone(id, zoneData);
        toast.success(t('zone.updateSuccess'));
      } else {
        await zoneService.createZone(zoneData);
        toast.success(t('zone.createSuccess'));
      }

      navigate('/zones');
    } catch (error: unknown) {
      console.error('Error saving zone:', error);
      const err = error as { response?: { data?: { errors?: Record<string, string>; message?: string } } };
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
      toast.error(err.response?.data?.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/zones');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            {isEditMode ? t('zone.editZone') : t('zone.createNew')}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('zone.fields.name')}
                  value={formData.name}
                  onChange={handleChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>

              {/* Coordinates */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('zone.fields.centerLatitude')}
                  type="number"
                  inputProps={{ step: '0.00000001', min: -90, max: 90 }}
                  value={formData.centerLatitude}
                  onChange={handleChange('centerLatitude')}
                  error={!!errors.centerLatitude}
                  helperText={errors.centerLatitude}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('zone.fields.centerLongitude')}
                  type="number"
                  inputProps={{ step: '0.00000001', min: -180, max: 180 }}
                  value={formData.centerLongitude}
                  onChange={handleChange('centerLongitude')}
                  error={!!errors.centerLongitude}
                  helperText={errors.centerLongitude}
                  required
                />
              </Grid>

              {/* Radius */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('zone.fields.radiusKm')}
                  type="number"
                  inputProps={{ step: '1', min: 1, max: 1000 }}
                  value={formData.radiusKm}
                  onChange={handleChange('radiusKm')}
                  error={!!errors.radiusKm}
                  helperText={errors.radiusKm || `${formData.radiusKm} km`}
                  required
                />
              </Grid>

              {/* Color */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('zone.fields.color')}
                  type="color"
                  value={formData.color}
                  onChange={handleChange('color')}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('zone.fields.description')}
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleChange('description')}
                />
              </Grid>

              {/* Visibility */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isVisible}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={t('zone.fields.isVisible')}
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
              >
                {loading ? t('common.loading') : t('common.save')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
  );
};

export default ZoneFormPage;
